// ============================================================
// Die schwebende Kopfleiste (SPEC §13.2, Muster lilasprojekte TopNav.tsx).
//
//   Marke · Reiter (Kataloge · Bibliothek · Admin) · Thema · Konto
//
// Sie SCHWEBT über allem (`position: fixed`, 16 px Luft ringsum, Glas). Jeder
// Bildschirm rechnet seinen Freiraum aus `--kopf-frei` in base.css, nicht aus einer
// eigenen Zahl. Im Katalog und im Modell legt der Bildschirm darunter die
// `Unterleiste` mit dem Projektnamen (§13.2).
//
// „Admin" erscheint nur für Admins (§2.3) — eine Leiste, die je nach Rolle anders
// lang ist, ist hier in Ordnung, weil die Admin-Seiten ohnehin nur Admins öffnen.
// ============================================================
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar, Icon, IconButton } from "../ui";
import { Logo } from "./Logo";
import { useIch } from "../../api/hooks/basis";
import { useT } from "../../lib/texte";
import { istAdmin } from "../../lib/rechte";
import { useTheme } from "../../lib/theme";
import { reiterAn, weg } from "../../lib/wege";
import { useAuthStore } from "../../store/useAuthStore";

export interface Reiter {
  zu: string;
  text: string;
  icon: string;
  genau?: boolean;
}

/** Die Reiter der Leisten (§13.2): Kataloge · Bibliothek · (Admin). */
export function reiterFuer(admin: boolean, t: ReturnType<typeof useT>["t"]): Reiter[] {
  const r: Reiter[] = [
    { zu: weg.kataloge(), text: t.nav.kataloge, icon: "book-open", genau: true },
    { zu: weg.bibliothek(), text: t.nav.bibliothek, icon: "books" },
  ];
  if (admin) r.push({ zu: "/admin", text: t.nav.admin, icon: "gear" });
  return r;
}

export function TopNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const qc = useQueryClient();
  const status = useAuthStore((s) => s.status);
  const abmelden = useAuthStore((s) => s.abmelden);
  const angemeldet = status === "angemeldet";
  const { t } = useT();
  const { thema, umschalten } = useTheme();
  const { data: ich } = useIch(angemeldet);
  const [kontoOffen, setKontoOffen] = useState(false);
  const kontoRef = useRef<HTMLDivElement>(null);

  // Das Kontomenü schließt bei Klick daneben und bei Escape.
  useEffect(() => {
    if (!kontoOffen) return;
    const beiDruck = (e: MouseEvent) => {
      if (!kontoRef.current?.contains(e.target as Node)) setKontoOffen(false);
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") setKontoOffen(false);
    };
    document.addEventListener("mousedown", beiDruck);
    document.addEventListener("keydown", beiTaste);
    return () => {
      document.removeEventListener("mousedown", beiDruck);
      document.removeEventListener("keydown", beiTaste);
    };
  }, [kontoOffen]);

  const reiter = reiterFuer(istAdmin(ich?.rolle), t);

  return (
    <header className="kopf glass">
      <Logo />
      {angemeldet && (
        <nav className="kopf-nav mobile-hide" aria-label={t.nav.bereiche}>
          {reiter.map((p) => {
            const an = reiterAn(p.zu, pathname, p.genau);
            return (
              <Link key={p.zu} to={p.zu} aria-current={an ? "page" : undefined} className={`navlink ring-focus${an ? " aktiv" : ""}`} title={p.text}>
                <Icon name={p.icon} size={17} />
                <span className="navlink-text">{p.text}</span>
              </Link>
            );
          })}
        </nav>
      )}
      <div style={{ flex: 1 }} />
      <IconButton
        icon={thema === "dark" ? "sun" : "moon"}
        title={thema === "dark" ? t.nav.themaHell : t.nav.themaDunkel}
        onClick={umschalten}
      />
      {angemeldet && ich && (
        <div ref={kontoRef} style={{ position: "relative" }}>
          <Avatar person={ich} size={32} ring onClick={() => setKontoOffen((o) => !o)} title={t.nav.konto} />
          {kontoOffen && (
            <div className="konto-menue anim-pop" role="menu">
              <div className="kopfzeile">
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{ich.anzeigename}</div>
                <div className="hinweiszeile">
                  {ich.email} · {t.rollen[ich.rolle]}
                </div>
              </div>
              <Link to={weg.profil()} role="menuitem" onClick={() => setKontoOffen(false)}>
                <Icon name="user-circle" size={16} />
                {t.nav.profil}
              </Link>
              {istAdmin(ich.rolle) && (
                <Link to={weg.admin()} role="menuitem" className="mobile-only" onClick={() => setKontoOffen(false)}>
                  <Icon name="gear" size={16} />
                  {t.nav.admin}
                </Link>
              )}
              <button
                type="button"
                role="menuitem"
                className="gefahr"
                onClick={() => {
                  setKontoOffen(false);
                  void abmelden().then(() => {
                    // Der Zwischenspeicher MUSS leer sein, bevor die Nächste sich
                    // anmeldet: sonst sähe sie einen Wimpernschlag lang die Kataloge
                    // der vorigen Person aus dem Cache.
                    qc.clear();
                    navigate("/anmelden", { replace: true });
                  });
                }}
              >
                <Icon name="sign-out" size={16} />
                {t.nav.abmelden}
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
